import { useMemo, useState } from "react";
import { useReadyAuth } from "@/auth";
import { shortDate } from "@/features/signals/format";
import { useMyMagicLinks, useRevokeMagicLink } from "@/features/magic-links/hooks";
import type { MagicLinkRow, MagicLinkStatus } from "@/features/magic-links/api";
import {
  EXPIRED_HISTORY_DAYS,
  filterMagicLinks,
  filterMagicLinksForReview,
  type LinkDateFilter,
  type LinkStatusFilter,
} from "@/features/magic-links/visibility";

/**
 * Magic Links management (W2, live). Replaces the Phase C placeholder. Two views
 * over the same live data: a flat link list and a "by signal" grouping. Each
 * row shows recipient, account, live open activity, expiry and status, with a
 * revoke control. Links are created from a signal's Share action
 * (create_magic_link); this surface reads them back through list_my_magic_links
 * and revokes them through revoke_magic_link.
 *
 * Colors reference the --bdse-* CSS variables (theme-adaptive) rather than the
 * fixed-light Tailwind bdse-* color utilities, so the table stays legible in
 * dark mode alongside the rest of the app's semantic surfaces.
 */

// Full literal utility strings so Tailwind's content scanner generates them
// (interpolating a prefix at runtime would leave the class ungenerated).
const TXT = "text-[color:var(--bdse-text)]";
const TXT2 = "text-[color:var(--bdse-text-secondary)]";
const TXT3 = "text-[color:var(--bdse-text-tertiary)]";
const BORDER = "border-[color:var(--bdse-border)]";

const STATUS_STYLE: Record<MagicLinkStatus, string> = {
  active: "text-[color:var(--bdse-teal)] border-bdse-teal/40 bg-bdse-teal/10",
  revoked: `${TXT3} ${BORDER} bg-[color:var(--bdse-bg-tertiary)]`,
  expired: "text-amber-600 border-amber-500/40 bg-amber-500/10",
};

type LinkView = "all" | "signal";

function StatusBadge({ status }: { status: MagicLinkStatus }): JSX.Element {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium capitalize ${STATUS_STYLE[status]}`}
    >
      {status}
    </span>
  );
}

interface SignalGroup {
  signalId: string;
  company: string;
  headline: string;
  links: MagicLinkRow[];
}

/** Group links by their signal, preserving the (created-desc) input order. */
function groupBySignal(links: MagicLinkRow[]): SignalGroup[] {
  const map = new Map<string, SignalGroup>();
  for (const l of links) {
    let g = map.get(l.signalId);
    if (!g) {
      g = { signalId: l.signalId, company: l.company, headline: l.headline, links: [] };
      map.set(l.signalId, g);
    }
    g.links.push(l);
  }
  return [...map.values()];
}

export function MagicLinks(): JSX.Element {
  const { profile } = useReadyAuth();
  const scope = profile.role === "admin" || profile.role === "super_admin" ? "all" : "mine";
  const { data: links, status } = useMyMagicLinks(scope);
  const revoke = useRevokeMagicLink();
  const [view, setView] = useState<LinkView>("all");
  const [statusFilter, setStatusFilter] = useState<LinkStatusFilter>("active");
  const [consultantQuery, setConsultantQuery] = useState("");
  const [dateFilter, setDateFilter] = useState<LinkDateFilter>("all");

  const activeCount = links?.filter((l) => l.status === "active").length ?? 0;
  const revokingId = revoke.isPending ? (revoke.variables as string) : null;
  const confirmRevoke = (link: MagicLinkRow) => {
    if (
      window.confirm(
        `Revoke the link for ${link.recipientName} on ${link.company}? They will lose access immediately.`
      )
    ) {
      revoke.mutate(link.id);
    }
  };

  const visibleLinks = useMemo(() => {
    const byStatus = filterMagicLinks(links ?? [], statusFilter);
    return filterMagicLinksForReview(byStatus, consultantQuery, dateFilter);
  }, [consultantQuery, dateFilter, links, statusFilter]);
  const groups = groupBySignal(visibleLinks);

  return (
    <div>
      <header className="bdse-page-header">
        <h1 className="bdse-page-title">Magic Links</h1>
        <p className="bdse-page-subtitle">
          Secure links that let an assigned colleague act on a signal without signing in
        </p>
      </header>

      {status === "pending" ? (
        <div className="bdse-glass-surface p-8">
          <p className={`text-sm ${TXT2}`}>Loading magic links…</p>
        </div>
      ) : status === "error" ? (
        <div className="bdse-glass-surface p-8">
          <p className={`text-sm ${TXT2}`}>Could not load magic links. Refresh to try again.</p>
        </div>
      ) : !links || links.length === 0 ? (
        <div className="bdse-glass-surface p-8 max-w-3xl">
          <h2 className={`mb-2 text-sm font-semibold ${TXT}`}>No magic links yet</h2>
          <p className={`text-sm leading-relaxed ${TXT2}`}>
            Open a signal, choose <strong>Share</strong>, and pick a recipient to create a link. It
            will appear here with its open activity.
          </p>
        </div>
      ) : (
        <div className="bdse-glass-surface magic-links-panel p-0">
          <div className="magic-links-toolbar">
            <div>
              <span className={`text-sm font-medium ${TXT}`}>
                {visibleLinks.length} shown · {activeCount} active
              </span>
              <p className={`magic-links-retention ${TXT3}`}>
                Expired links leave this view after {EXPIRED_HISTORY_DAYS} days; the audit record is
                retained.
              </p>
            </div>
            <div className="magic-links-controls">
              <label className="magic-links-filter magic-links-filter--search">
                <span>Consultant</span>
                <input
                  type="search"
                  value={consultantQuery}
                  placeholder="Search name or email"
                  onChange={(event) => setConsultantQuery(event.target.value)}
                />
              </label>
              <label className="magic-links-filter">
                <span>Assigned</span>
                <select
                  value={dateFilter}
                  onChange={(event) => setDateFilter(event.target.value as LinkDateFilter)}
                >
                  <option value="all">Any time</option>
                  <option value="week">This week</option>
                  <option value="month">This month</option>
                  <option value="quarter">This quarter</option>
                  <option value="year">This year</option>
                </select>
              </label>
              <label className="magic-links-filter">
                <span>Status</span>
                <select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value as LinkStatusFilter)}
                >
                  <option value="active">Active</option>
                  <option value="expired">Expired (90 days)</option>
                  <option value="all">All recent</option>
                </select>
              </label>
              <ViewToggle view={view} onChange={setView} />
            </div>
          </div>
          {visibleLinks.length === 0 ? (
            <p className={`magic-links-empty ${TXT2}`}>
              No {statusFilter === "all" ? "recent" : statusFilter} links.
            </p>
          ) : view === "all" ? (
            <ul className="magic-link-list">
              {visibleLinks.map((link) => (
                <LinkCard
                  key={link.id}
                  link={link}
                  showSignal
                  onRevoke={() => confirmRevoke(link)}
                  revoking={revokingId === link.id}
                />
              ))}
            </ul>
          ) : (
            <div className="magic-link-groups">
              {groups.map((group) => (
                <section key={group.signalId} className="magic-link-group">
                  <GroupHeader group={group} />
                  <ul className="magic-link-list">
                    {group.links.map((link) => (
                      <LinkCard
                        key={link.id}
                        link={link}
                        showSignal={false}
                        onRevoke={() => confirmRevoke(link)}
                        revoking={revokingId === link.id}
                      />
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ViewToggle({
  view,
  onChange,
}: {
  view: LinkView;
  onChange: (v: LinkView) => void;
}): JSX.Element {
  const opts: { id: LinkView; label: string }[] = [
    { id: "all", label: "All links" },
    { id: "signal", label: "By signal" },
  ];
  return (
    <div className={`inline-flex rounded-md border ${BORDER} p-0.5`}>
      {opts.map((o) => (
        <button
          key={o.id}
          type="button"
          aria-pressed={view === o.id}
          onClick={() => onChange(o.id)}
          className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
            view === o.id
              ? "bg-bdse-teal/15 text-[color:var(--bdse-teal)]"
              : `${TXT2} hover:text-[color:var(--bdse-text)]`
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function GroupHeader({ group }: { group: SignalGroup }): JSX.Element {
  const active = group.links.filter((l) => l.status === "active").length;
  return (
    <header className="magic-link-group__header">
      <div className={`magic-link-group__title ${TXT}`}>{group.headline || "Untitled signal"}</div>
      <div className={`magic-link-group__meta ${TXT3}`}>
        {group.company} · {group.links.length} link
        {group.links.length === 1 ? "" : "s"} · {active} active
      </div>
    </header>
  );
}

function LinkCard({
  link,
  showSignal,
  onRevoke,
  revoking,
}: {
  link: MagicLinkRow;
  showSignal: boolean;
  onRevoke: () => void;
  revoking: boolean;
}): JSX.Element {
  return (
    <li
      className={`magic-link-card ${showSignal ? "magic-link-card--with-signal" : "magic-link-card--compact"} ${TXT}`}
    >
      <div className="magic-link-card__recipient">
        <div className="font-medium">{link.recipientName}</div>
        {link.recipientEmail ? (
          <div className={`text-xs ${TXT3}`}>{link.recipientEmail}</div>
        ) : null}
      </div>
      {showSignal ? (
        <div className="magic-link-card__signal">
          <div className="magic-link-card__account">{link.company}</div>
          {link.headline ? (
            <div className={`magic-link-card__headline ${TXT3}`}>{link.headline}</div>
          ) : null}
        </div>
      ) : null}
      <div className="magic-link-card__status">
        <StatusBadge status={link.status} />
      </div>
      <dl className={`magic-link-card__facts ${TXT2}`}>
        <div>
          <dt>Opens</dt>
          <dd title={link.lastOpenedAt ? `Last opened ${shortDate(link.lastOpenedAt)}` : undefined}>
            {link.openCount || "—"}
          </dd>
        </div>
        <div>
          <dt>Created</dt>
          <dd>{shortDate(link.createdAt)}</dd>
        </div>
        <div>
          <dt>Expires</dt>
          <dd>{link.expiresAt ? shortDate(link.expiresAt) : "—"}</dd>
        </div>
      </dl>
      <div className="magic-link-card__action">
        {link.status === "active" ? (
          <button
            type="button"
            className={`inline-flex items-center gap-1 rounded-md border ${BORDER} px-2.5 py-1 text-xs font-medium ${TXT2} transition-colors hover:border-red-400/50 hover:text-red-600 disabled:opacity-50`}
            disabled={revoking}
            onClick={onRevoke}
          >
            {revoking ? "Revoking…" : "Revoke"}
          </button>
        ) : null}
      </div>
    </li>
  );
}
